/**
 * Facturación electrónica: el contrato con nuestro servicio.
 *
 * La cadena completa es
 * `Guerrera OPS → nuestro servicio → Plemsi → DIAN`, y de toda ella este
 * archivo solo conoce el primer tramo. Ni Plemsi ni la DIAN se mencionan aquí
 * a propósito: esa traducción es interna del servicio y puede cambiar de
 * proveedor sin que el POS se entere. Ya nos pasó una vez con Bold, y la única
 * razón por la que cambiar dolió poco fue que el mapeo vivía en un solo sitio.
 *
 * ---
 *
 * **La regla que define todo: el POS calcula los impuestos, el servicio los
 * verifica.**
 *
 * Qué tributo y qué tarifa lleva cada línea depende del canal, del producto y
 * del concepto de la venta, y eso solo lo sabe quien vende. El servicio no
 * interpreta normas tributarias: comprueba que la aritmética cierre y rechaza
 * con `422` lo que no cuadre, **antes** de tocar la DIAN.
 *
 * Por eso aquí abajo hay un portero propio —`reparos`—, que hace la misma
 * verificación antes de que la petición salga. No sobra: un 422 gasta un viaje
 * de ida y vuelta y llega como un error que el cajero no puede interpretar. Un
 * reparo local llega como una frase en español mientras el pedido todavía está
 * abierto en la pantalla.
 *
 * ---
 *
 * **Los centavos.** Todo el resto de la plataforma trabaja en pesos enteros
 * —`pesos()` redondea, y el dinero nunca lleva decimales—. Aquí no se puede.
 * El servicio verifica que `base × tarifa` dé el valor declarado, y con pesos
 * enteros eso casi nunca cuadra: una empanada de $3.700 con INC 8% tiene base
 * $3.426 y 3.426 × 8 % = 274,08, que no es 274.
 *
 * Así que el cuerpo de la factura se arma con **dos decimales** y el redondeo
 * se hace una sola vez, al final. Es la única parte del sistema donde el dinero
 * tiene centavos, y tiene que seguir siéndolo: mezclar las dos formas de
 * redondear es exactamente cómo se fabrica un rechazo que nadie entiende.
 */
export const TIPOS_DOCUMENTO_FISCAL = [
    {
        valor: 'comprobante',
        texto: 'Comprobante de venta',
        nota: 'Soporte interno con su número y su detalle. No pasa por la DIAN y no sirve para descontar impuestos.',
    },
    {
        valor: 'factura',
        texto: 'Factura electrónica',
        nota: 'Documento ante la DIAN, con CUFE y numeración autorizada. Solo cuando el emisor está habilitado.',
    },
    {
        valor: 'pos',
        texto: 'Documento equivalente POS',
        nota: 'El tiquete de caja, con numeración propia. Todavía no se emite: Plemsi lo tiene habilitado, '
            + 'nuestro facturador aún no expone la ruta.',
    },
];
/**
 * ¿Se puede emitir este tipo de documento hoy?
 *
 * El tiquete POS está en el modelo y **no se emite todavía**, y conviene que
 * eso viva en una función y no en la cabeza de nadie. La habilitación ante la
 * DIAN ya está resuelta —Plemsi la muestra activa desde el 22 de septiembre de
 * 2026— pero el facturador solo expone `/v1/facturas`, `/v1/notas-credito` y
 * `/v1/notas-debito`. Sin ruta no hay tiquete.
 *
 * Está aquí, a medio construir y dicho, en vez de fuera: cuando aparezca la
 * ruta, lo que hay que cambiar es esta línea y el mapeador, no rastrear dónde
 * se asumió que solo existían dos tipos de documento.
 */
export function seEmiteHoy(t) {
    return t !== 'pos';
}
export function textoTipoDocumento(t) {
    return TIPOS_DOCUMENTO_FISCAL.find((x) => x.valor === t)?.texto ?? t;
}
// --------------------------------------------------- el consumidor final
/**
 * A quién se le factura cuando nadie dio sus datos.
 *
 * Nadie dicta su cédula para comprar una empanada, y durante un tiempo este
 * código dio por hecho que eso significaba que esas ventas no se podían
 * facturar. Está escrito así en un reparo que decía, con todas sus letras, que
 * «una factura electrónica no se puede emitir a Consumidor Final». **Es falso,
 * y era mío.** La DIAN tiene previsto justo ese caso desde hace años: existe un
 * número genérico para el adquiriente no identificado y la factura sale a su
 * nombre. Así funcionan los demás sistemas del mercado.
 *
 * El número es **222222222222 — doce dígitos**, con tipo de documento 13
 * (cédula de ciudadanía) y razón social «Consumidor final», según la Resolución
 * 0227 de 2025. No hay tope de monto: el límite de 5 UVT que se cita a menudo
 * es del tiquete POS, que es otro documento.
 *
 * > **La cantidad de dígitos está en disputa en las fuentes secundarias** —hay
 * > textos que hablan de nueve o de diez, arrastrando la Resolución 000042 de
 * > 2020—, así que esto se comprueba contra el facturador en pruebas antes de
 * > darlo por bueno. Ahí la autoridad es lo que Plemsi acepte, no lo que diga
 * > un artículo.
 *
 * ---
 *
 * **Es una constante y no un cliente sembrado en cada empresa, y esa es la
 * decisión que importa.** Como registro en `empresas/{empresa}/clientes` habría
 * que crearlo al dar de alta cada empresa —y las que ya existían se quedarían
 * sin él—, alguien podría borrarlo sin querer, otro podría editarle el número,
 * y acabarían habiendo tantas versiones del consumidor final como empresas.
 * Aquí hay una sola definición, existe para toda empresa desde el primer día
 * sin que nadie la cree, y no se puede romper.
 */
export const CONSUMIDOR_FINAL = {
    nombre: 'Consumidor final',
    tipo_documento: '13',
    documento: '222222222222',
};
/** ¿Esta venta va al genérico de la DIAN y no a un cliente con nombre propio? */
export function esConsumidorFinal(documento) {
    return (documento ?? '').trim() === CONSUMIDOR_FINAL.documento;
}
/**
 * Los tres campos fiscales que se guardan en la venta al cobrar.
 *
 * Se resuelven **en el momento del cobro y se copian al documento**, no se
 * dejan como una referencia a resolver después. Una venta tiene que poder
 * facturarse mirándola a ella sola: si el adquiriente hubiera que ir a buscarlo
 * a otra parte, una venta de hace tres meses se facturaría con los datos que el
 * cliente tenga hoy, que no son los que tenía cuando compró.
 *
 * Sin documento no se inventa nada: se usa el consumidor final. Es lo que hace
 * que el mostrador tenga adquiriente siempre, que es lo que faltaba.
 */
export function datosFiscalesDeVenta(c) {
    const doc = c?.documento?.trim();
    if (!doc) {
        return {
            cliente_nombre: CONSUMIDOR_FINAL.nombre,
            cliente_tipo_documento: CONSUMIDOR_FINAL.tipo_documento,
            cliente_documento: CONSUMIDOR_FINAL.documento,
        };
    }
    return {
        cliente_nombre: c?.nombre?.trim() || CONSUMIDOR_FINAL.nombre,
        cliente_tipo_documento: c?.tipo_documento?.trim() || '13',
        cliente_documento: doc,
    };
}
/**
 * ¿Esta venta va en cero?
 *
 * **Una venta en cero nunca es una factura electrónica: es un comprobante.** La
 * DIAN rechaza un documento sin valor, y mandarlo quemaria un consecutivo de la
 * resolucion para que lo devuelvan. Pero la venta existe y hay que cerrarla:
 * la degustacion, la reposicion de un producto que salio malo, el pedido que se
 * entrego sin cobrar. Son ventas de verdad —descuentan inventario y hay que
 * saber cuanto se regalo— y cierran su ciclo con comprobante.
 *
 * Vive aqui, en una funcion con nombre, porque la regla la aplican tres sitios
 * que no se hablan entre si: el mostrador al cobrar, la entrega de un pedido, y
 * la bandeja de pendientes. Escrita tres veces, se arregla en una y se queda
 * mal en dos.
 *
 * Un total que no es numero no es «cero»: es un dato que falta, y eso lo
 * atrapan `reparos` y la bandeja, no esto.
 */
export function ventaEnCero(total) {
    return typeof total === 'number' && total <= 0;
}
/**
 * Lo que hay que estamparle a un documento que salio sin adquirente.
 *
 * Devuelve `null` cuando ya lo tiene, y los tres campos cuando no. Es la misma
 * decision de `datosFiscalesDeVenta` mirada al reves: alli se resuelve al
 * cobrar, aqui se repara lo que se grabo antes de que eso existiera.
 *
 * **Por que hace falta una funcion aparte.** Un pedido sin documento no se
 * factura y tampoco entra a «Por facturar» —`leCorrespondeFactura` lo descarta
 * en su primera condicion—, asi que no queda ni emitido ni pendiente: se cae
 * del sistema sin dejar rastro. Los pedidos nuevos ya nacen sellados desde la
 * pantalla; los viejos, y los que se entreguen mas tarde, se reparan con esto.
 *
 * **La razon social se conserva si la hay.** El numero generico identifica a
 * quien no dio sus datos; si el cliente si tiene nombre fiscal, ese nombre es
 * mas cierto que «Consumidor final» y no hay por que perderlo. Es la misma
 * regla que ya aplica la pantalla de pedidos al crearlos.
 */
export function selloConsumidorFinal(d) {
    if (d.cliente_documento?.trim())
        return null;
    return {
        cliente_tipo_documento: CONSUMIDOR_FINAL.tipo_documento,
        cliente_documento: CONSUMIDOR_FINAL.documento,
        cliente_razon_social: d.cliente_razon_social?.trim() || CONSUMIDOR_FINAL.nombre,
    };
}
// ----------------------------------------------- enlaces y codigo QR
/**
 * Un enlace que de verdad se puede abrir, o nada.
 *
 * **Existe por un sintoma que no se parecia a su causa:** desde Ventas, hacer
 * clic en «Factura 1004» mandaba al usuario al Inicio. No era un problema de
 * permisos ni de rutas. El facturador puede devolver el enlace del PDF como una
 * direccion relativa —`documentos/abc.pdf`—, y el navegador la resuelve contra
 * la pagina actual: se va a `/documentos/abc.pdf`, el hosting responde el
 * `index.html` de siempre porque todo lo que no existe cae ahi, la aplicacion
 * arranca sin `#` y se dibuja el Inicio. Un enlace roto que parece una pantalla
 * que se equivoca.
 *
 * Solo pasan `http://` y `https://`. Lo demas no es un enlace que este programa
 * pueda abrir, y mas vale no pintarlo que pintar uno que se lleva al usuario a
 * otra parte.
 */
export function enlaceExterno(u) {
    const t = (u ?? '').trim();
    return /^https?:\/\//i.test(t) ? t : undefined;
}
/** El catalogo donde la DIAN deja consultar un documento ya validado. */
export const CATALOGO_DIAN = 'https://catalogo-vpfe.dian.gov.co/document/searchqr';
/**
 * La direccion con la que se valida una factura en el catalogo de la DIAN.
 *
 * Es `…/searchqr?documentkey=<CUFE>`. El CUFE es la llave: identifica el
 * documento y no hay nada mas que mandar.
 *
 * **Un documento emitido en pruebas no esta en este catalogo.** El catalogo de
 * la DIAN publica lo de produccion; lo que se emite contra el ambiente de
 * habilitacion existe para la DIAN pero no se consulta aqui. Por eso esto
 * devuelve `null` cuando el documento es de pruebas, en vez de un enlace que
 * siempre va a decir «no encontrado»: un enlace que no funciona se lee como un
 * sistema roto, y lo que pasa es otra cosa que hay que decir con palabras.
 */
export function urlDeValidacionDIAN(cufe, ambiente) {
    const c = (cufe ?? '').trim();
    if (!c)
        return null;
    if ((ambiente ?? '').toLowerCase() === 'pruebas')
        return null;
    return `${CATALOGO_DIAN}?documentkey=${encodeURIComponent(c)}`;
}
/**
 * La direccion que viene dentro del contenido del QR, si viene alguna.
 *
 * El facturador puede devolver el QR de dos formas. Una es la direccion sola.
 * La otra es el bloque de campos del anexo tecnico —`NumFac`, `FecFac`,
 * `NitFac`, `DocAdq`, `ValFac`, … y al final la direccion de consulta—, que es
 * la forma del anexo 1.7 de 2020.
 *
 * **Esa diferencia es la que hace que un QR «no sirva para validar».** El
 * bloque de campos es texto: la camara del telefono lo escanea, muestra
 * cuatrocientos caracteres y no ofrece abrir nada. La direccion sola abre el
 * catalogo de la DIAN, que es lo que alguien quiere cuando apunta la camara a
 * una factura. El anexo vigente llama a esa seccion «URL QRCode», en singular.
 *
 * Asi que se extrae la direccion y se codifica esa. Lo demas del bloque ya esta
 * impreso en el papel, en letras, encima del codigo.
 */
export function urlDentroDelQR(contenido) {
    const t = (contenido ?? '').trim();
    if (!t)
        return null;
    const m = t.match(/https?:\/\/[^\s"'<>]+/i);
    return m ? m[0] : null;
}
// --------------------------------------------------- valores admitidos
/**
 * Lo que el servicio acepta, palabra por palabra.
 *
 * Están copiados del contrato y no deducidos: un valor que no reconozca
 * devuelve `422` con la lista de los válidos, y descubrirlo en producción con
 * un cliente esperando el papel es la peor forma de enterarse.
 */
export const TIPOS_IDENTIFICACION_DIAN = [
    'NIT', 'CEDULA', 'CEDULA_EXTRANJERIA', 'PASAPORTE', 'PPT', 'NUIP',
];
export const TIPOS_IMPUESTO_DIAN = [
    'IVA', 'INC', 'ICA', 'IC', 'INC_BOLSAS', 'IBUA', 'ICUI', 'NO_APLICA',
];
export const FORMAS_PAGO_DIAN = ['CONTADO', 'CREDITO'];
export const METODOS_PAGO_DIAN = [
    'EFECTIVO', 'TRANSFERENCIA', 'CONSIGNACION',
    'TARJETA_CREDITO', 'TARJETA_DEBITO', 'CHEQUE',
];
export const TIPOS_ORGANIZACION_DIAN = ['PERSONA_JURIDICA', 'PERSONA_NATURAL'];
export const RESPONSABILIDADES_DIAN = [
    'REGIMEN_SIMPLE', 'GRAN_CONTRIBUYENTE', 'AUTORRETENEDOR', 'NO_RESPONSABLE', 'NO_APLICA',
];
/**
 * Del código DIAN que guarda la ficha del cliente al nombre que pide el servicio.
 *
 * La plataforma guarda el código numérico de la DIAN —`31` para NIT, `13` para
 * cédula— porque es lo que va en el documento fiscal. El servicio recibe
 * nombres. Traducir aquí y no en la pantalla es lo que permite que el día que
 * el cliente corrija su tipo de documento, la factura salga bien sin tocar
 * nada más.
 *
 * Devuelve `null` para los que el servicio no admite —tarjeta de identidad,
 * registro civil, NIT extranjero—. No es un descuido: son documentos con los
 * que no se factura, y devolver algo parecido sería peor que decir que no.
 */
export function identificacionDIAN(codigo) {
    switch (codigo) {
        case '31': return 'NIT';
        case '13': return 'CEDULA';
        case '22': return 'CEDULA_EXTRANJERIA';
        case '41': return 'PASAPORTE';
        case '47':
        case '48': return 'PPT';
        case '91': return 'NUIP';
        default: return null;
    }
}
/**
 * Del método de pago del mostrador al que entiende la DIAN.
 *
 * Los métodos del mostrador los administra cada sede y pueden llamarse como
 * quieran —«Bold», «Nequi», «la tarjeta del señor»—, así que el mapeo va por el
 * **tipo**, que sí es una lista cerrada, y cae en efectivo cuando no se sabe.
 *
 * Caer en efectivo y no en algo más específico es deliberado: es el método que
 * menos afirma. Declarar «tarjeta de crédito» cuando fue débito es decirle a la
 * DIAN algo que no pasó.
 */
export function metodoPagoDIAN(tipo) {
    switch (tipo) {
        case 'tarjeta': return 'TARJETA_DEBITO';
        case 'transferencia': return 'TRANSFERENCIA';
        case 'consignacion': return 'CONSIGNACION';
        case 'cheque': return 'CHEQUE';
        default: return 'EFECTIVO';
    }
}
/**
 * Del impuesto del catálogo al del documento fiscal.
 *
 * `excluido` se manda como IVA con tarifa y valor en cero, que es lo que dice
 * el contrato. Podría parecer que corresponde `NO_APLICA`, y no: una línea
 * excluida sí tiene un tributo asociado —el IVA— del que está excluida, y así
 * es como la DIAN espera verla.
 */
export function impuestoDIAN(tipo) {
    if (tipo === 'INC')
        return 'INC';
    return 'IVA';
}
/**
 * Cuánto queda por acreditar de una factura.
 *
 * Una nota crédito no puede acreditar más de lo facturado, y el tope baja con
 * cada nota que ya exista. El facturador lo comprueba y devuelve `422
 * NOTA_EXCEDE_FACTURA` — pero eso es la última red, no la primera: un error
 * atrapado en el formulario, antes de escribir el valor, vale más que uno
 * atrapado después de un viaje de ida y vuelta.
 *
 * **Cuentan las `ACEPTADO` y las `EN_PROCESO`.** Una `EN_PROCESO` tiene CUFE:
 * el documento existe ante la DIAN aunque falte el veredicto, y lo que acredita
 * ya está comprometido. Dejarla fuera permitiría acreditar dos veces lo mismo
 * mientras la DIAN se decide.
 *
 * Las `RECHAZADO` no cuentan: no se creó nada.
 *
 * Devuelve `null` cuando no se sabe el total de la factura — facturas anteriores
 * a que esto se guardara—. `null` no es «queda todo»: es «no lo sé», y la
 * pantalla tiene que decirlo en vez de dejar escribir cualquier cifra.
 */
export function saldoAcreditable(totalFactura, notas = []) {
    if (typeof totalFactura !== 'number' || !(totalFactura > 0))
        return null;
    const usado = notas
        .filter((n) => n.tipo !== 'debito')
        .filter((n) => n.estado === 'ACEPTADO' || n.estado === 'EN_PROCESO')
        .reduce((s, n) => s + (n.total ?? 0), 0);
    return Math.max(0, totalFactura - usado);
}
export const ESTADOS_DOCUMENTO = [
    {
        valor: 'PENDIENTE', texto: 'Por facturar', tono: 'pendiente',
        nota: 'No salió nada hacia la DIAN. Se reintenta solo, sin riesgo de duplicar.',
    },
    {
        valor: 'ENVIANDO', texto: 'Enviando', tono: 'pendiente',
        nota: 'La petición salió y todavía no vuelve.',
    },
    {
        valor: 'EN_PROCESO', texto: 'En validación', tono: 'pendiente',
        nota: 'Llegó a la DIAN y tiene CUFE; falta el veredicto. Se consulta, nunca se reenvía.',
    },
    {
        valor: 'ACEPTADO', texto: 'Aceptada por la DIAN', tono: 'ok',
        nota: 'El documento existe, tiene CUFE y sirve de soporte fiscal.',
    },
    {
        valor: 'RECHAZADO', texto: 'Rechazada', tono: 'malo',
        nota: 'La DIAN la devolvió con sus reglas. No se creó ningún documento; ese número quedó quemado y se emite uno nuevo.',
    },
    {
        valor: 'SIN_RESPUESTA', texto: 'Sin confirmar', tono: 'pendiente',
        nota: 'No hubo respuesta y el documento pudo o no haberse emitido. No se reintenta: primero se consulta por la referencia.',
    },
];
export function textoEstadoDocumento(e) {
    return ESTADOS_DOCUMENTO.find((x) => x.valor === e)?.texto ?? e;
}
export function notaEstadoDocumento(e) {
    return ESTADOS_DOCUMENTO.find((x) => x.valor === e)?.nota ?? '';
}
export function tonoEstadoDocumento(e) {
    return ESTADOS_DOCUMENTO.find((x) => x.valor === e)?.tono ?? 'gris';
}
// ------------------------------------------- qué se puede hacer con cada uno
/**
 * Diez minutos. Pasados, un `ENVIANDO` deja de significar «va en camino».
 *
 * Un contenedor se puede morir a mitad de una petición, y entonces el documento
 * se queda en `ENVIANDO` para siempre — bloqueado, porque nadie puede tocar algo
 * que parece estar en curso. Pasado el plazo se trata como lo que es: no se
 * sabe qué pasó. Que es exactamente `SIN_RESPUESTA`.
 */
export const MINUTOS_COLGADA = 10;
export function colgada(f, ahora = Date.now()) {
    if (f?.estado !== 'ENVIANDO')
        return false;
    if (!f.emitida_local)
        return true;
    return ahora - f.emitida_local > MINUTOS_COLGADA * 60_000;
}
/**
 * ¿Se puede reintentar **solo**, sin que una persona mire?
 *
 * Solo cuando consta que nada salió: un documento que nunca se intentó, o uno
 * que el facturador rechazó en la puerta —llave ausente, llave revocada, sin
 * permiso—. Esos fallos no llegaron ni cerca de la DIAN y se arreglan solos en
 * cuanto alguien corrija la configuración.
 *
 * `RECHAZADO` queda por fuera aposta, aunque tampoco haya creado nada: lo
 * rechazaron por los datos, y reintentar los mismos datos da el mismo rechazo.
 * Reintentarlo solo sería una máquina insistiendo cada hora en algo que nunca
 * va a funcionar, y tapando en la lista lo que sí necesita a alguien.
 */
export function seReintentaSolo(f) {
    if (!f?.estado)
        return true;
    return f.estado === 'PENDIENTE';
}
/**
 * El único camino es consultar. Nunca reenviar.
 *
 * Dos estados llegan aquí por razones opuestas y con el mismo desenlace.
 * `SIN_RESPUESTA` es «no sé si existe»; `EN_PROCESO` es «existe, tengo el
 * CUFE, falta el veredicto». En los dos casos reenviar crea un segundo
 * documento ante la DIAN, y un documento ante la DIAN no se borra.
 */
export function soloConsultar(f) {
    return f?.estado === 'SIN_RESPUESTA' || f?.estado === 'EN_PROCESO';
}
/**
 * ¿Hay ya un documento ante la DIAN por esta venta?
 *
 * `EN_PROCESO` cuenta, y esa es la diferencia con `facturaLista`. La factura
 * existe y tiene CUFE: no está pendiente de emisión y no debe aparecer en la
 * bandeja de lo que falta por facturar. Lo que le falta es el veredicto, que
 * es otra lista y otra acción.
 */
export function tieneDocumento(f) {
    return f?.estado === 'ACEPTADO' || f?.estado === 'EN_PROCESO';
}
/** Espera a que una persona corrija algo. */
export function esperaPersona(f) {
    return f?.estado === 'RECHAZADO';
}
export function facturaLista(f) {
    return f?.estado === 'ACEPTADO';
}
/**
 * Qué hay que hacer con esto, en una frase, para quien mire la bandeja.
 *
 * La bandeja existe para que alguien decida, y una lista de estados en
 * mayúsculas no ayuda a decidir nada.
 */
export function queHacerCon(f) {
    if (!f?.estado)
        return 'Nunca se ha intentado facturar. Se puede mandar.';
    switch (f.estado) {
        case 'ACEPTADO': return 'Nada: ya está facturado.';
        case 'EN_PROCESO': return 'La factura existe y tiene CUFE. Falta el veredicto de la DIAN: se consulta, nunca se reenvía.';
        case 'PENDIENTE': return 'Se reintenta solo. Si lleva días aquí, revisa la configuración de facturación.';
        case 'ENVIANDO': return colgada(f)
            ? 'Lleva demasiado tiempo enviando. Hay que consultarla: el envío pudo completarse.'
            : 'Va en camino. Espera.';
        case 'RECHAZADO': return 'Corrige lo que dice el mensaje y vuelve a mandarla.';
        case 'SIN_RESPUESTA': return 'Consúltala primero. Reenviarla sin consultar puede emitirla dos veces.';
        default: return '';
    }
}
/**
 * ¿Ya tiene documento fiscal, emitido por otro?
 *
 * Pide `numero` y no solo que el campo exista: un `factura_externa` a medio
 * llenar —el objeto puesto y el número en blanco— sacaría la venta de la
 * bandeja sin dejar con qué cruzarla, que es la única cosa peor que tenerla
 * ahí para siempre.
 */
export function facturadaPorFuera(d) {
    return Boolean(d?.factura_externa?.numero?.trim());
}
/** Una línea para la pantalla: «bold · IGRS 312 · 2026-09-17». */
export function textoFacturaExterna(e) {
    return [e.sistema, e.numero, e.fecha].filter(Boolean).join(' · ');
}
/**
 * Las numeraciones como lista, venga el objeto nuevo o el arreglo viejo.
 *
 * El orden lo fija `documentos` cuando viene, porque el facturador sabe en qué
 * orden tienen sentido —la factura primero, las notas después— y un objeto no
 * garantiza orden por sí mismo. Lo que no esté en `documentos` va al final en
 * vez de desaparecer: una numeración que existe y no se muestra es peor que una
 * fuera de orden.
 */
export function listaDeNumeraciones(e) {
    const n = e?.numeraciones;
    if (!n)
        return [];
    if (Array.isArray(n))
        return n;
    const orden = e?.documentos ?? [];
    const tipos = Object.keys(n);
    tipos.sort((a, b) => {
        const ia = orden.indexOf(a);
        const ib = orden.indexOf(b);
        return (ia < 0 ? orden.length : ia) - (ib < 0 ? orden.length : ib);
    });
    return tipos.map((t) => ({ ...n[t], tipo: n[t].tipo ?? t }));
}
/** La numeración de las facturas, que es la que decide el ambiente. */
export function numeracionDeFactura(e) {
    const l = listaDeNumeraciones(e);
    return l.find((x) => (x.tipo ?? '').toUpperCase().startsWith('FACTURA')) ?? l[0];
}
/**
 * En pruebas el prefijo es `SETT` y los documentos **no tienen validez fiscal**.
 *
 * Tres fuentes, en orden de confianza: el booleano del servicio, el prefijo
 * —`SETT` es el de habilitación de la DIAN y no se usa para otra cosa— y, de
 * último, la cadena del ambiente.
 *
 * **Sin información se asume pruebas.** Es la única dirección segura del error:
 * marcar de prueba algo real hace que alguien pregunte; dar por real algo de
 * prueba hace que un cliente reciba un papel que su contador no va a encontrar
 * en la DIAN, y eso se descubre semanas después.
 */
export function esPruebas(e) {
    if (!e)
        return true;
    if (typeof e.es_produccion === 'boolean')
        return !e.es_produccion;
    if (e.prefijo)
        return e.prefijo.toUpperCase().startsWith('SETT');
    return (e.ambiente ?? '').toLowerCase() !== 'produccion';
}
/**
 * El prefijo que está usando el emisor, mirando también las numeraciones.
 *
 * Se enseña al lado del ambiente porque es el dato que delata una configuración
 * mal puesta: **un prefijo `SETT` en una empresa que se cree en producción es
 * el error que hay que hacer imposible de pasar por alto.**
 */
/**
 * ¿Este documento lleva el sello de PRUEBA en el papel?
 *
 * **Lo decide el documento, no el estado de la aplicación.** Cada respuesta de
 * emisión trae su propio `ambiente`, escrito por el facturador en el momento de
 * emitir. El reflejo que guarda la configuración puede tener horas: entre una
 * consulta del emisor y la siguiente, una empresa puede haber pasado a
 * producción. Si el papel saliera de ahí, una factura real podría imprimirse
 * con el sello de prueba —molesto— o, mucho peor, una de prueba podría salir
 * sin él, y esa diferencia solo aparece cuando el contador la busca en la DIAN
 * y no está.
 *
 * El respaldo es el estado de la aplicación, y solo cuando el documento no dice
 * nada: documentos emitidos antes de que esto se guardara, o un comprobante,
 * que no pasa por el facturador y por tanto no tiene ambiente propio.
 *
 * Sin ninguna de las dos fuentes, se sella. Marcar de prueba algo real hace que
 * alguien pregunte; dar por real algo de prueba no lo descubre nadie.
 */
export function selloDePrueba(documento, respaldo) {
    const a = (documento?.ambiente ?? '').trim().toLowerCase();
    if (a)
        return a !== 'produccion';
    return respaldo ?? true;
}
export function prefijoVigente(e) {
    // El de la FACTURA y no «el primero». Desde que hay un prefijo por tipo de
    // documento —GUER para facturas, NC para notas crédito—, «el primero» depende
    // del orden en que vengan las llaves del objeto. Y el que delata el ambiente
    // es el de la factura: `SETT` es el prefijo de habilitación de la DIAN.
    return e?.prefijo || numeracionDeFactura(e)?.prefijo || '';
}
export function numerosQueQuedan(n) {
    // Si el facturador lo dice, se le cree: lo sabe mejor que una resta hecha
    // sobre un rango que puede estar incompleto.
    if (typeof n.numeros_disponibles === 'number')
        return Math.max(0, n.numeros_disponibles);
    return Math.max(0, (n.hasta ?? 0) - (n.siguiente ?? 0) + 1);
}
/**
 * Cuánto se ha gastado del rango, en porcentaje.
 *
 * Existe para poder avisar al 80 %. Pedir una resolución nueva es un trámite, y
 * quedarse sin números es quedarse sin poder facturar — un lunes, con el
 * camión cargado.
 */
export function gastadoDelRango(n) {
    /*
     * **`null` cuando no se puede calcular, y no cero.**
     *
     * El contrato nuevo manda cuántos números quedan pero no dónde empieza ni
     * dónde va el rango, así que el porcentaje gastado no se puede deducir.
     * Devolver `0 %` sería decir «no has gastado nada» —una tranquilidad falsa
     * justo en el dato que existe para avisar al 80 %— y la alarma no sonaría
     * nunca. `null` obliga a la pantalla a decir que no lo sabe.
     */
    const total = (n.hasta ?? 0) - (n.desde ?? 0) + 1;
    if (total <= 0 || n.siguiente === undefined)
        return null;
    return Math.min(100, Math.round(((n.siguiente - (n.desde ?? 0)) / total) * 100));
}
export const PERMISOS_LLAVE = [
    { valor: 'emitir', texto: 'Emitir', nota: 'Crear facturas y notas. Es lo único que necesita un punto de venta.' },
    { valor: 'consultar', texto: 'Consultar', nota: 'Ver el emisor y los documentos ya emitidos.' },
    { valor: 'admin', texto: 'Administrar llaves', nota: 'Crear y revocar otras llaves. Solo para la llave que administra.' },
];
export function llaveViva(l) {
    return !l.revocada_en;
}
// ------------------------------------------------------------ el traductor
/** Dos decimales. Solo aquí; el resto de la plataforma trabaja en pesos enteros. */
export function centavos(n) {
    return Math.round((n + Number.EPSILON) * 100) / 100;
}
/**
 * Una línea del documento fiscal, a partir de una línea de venta.
 *
 * El precio de la plataforma lleva el impuesto adentro, así que aquí se parte:
 * `base = total / (1 + tasa)` y el impuesto es lo que sobra. Se hace con dos
 * decimales y sobre el total de la línea —no por unidad y multiplicado—, porque
 * redondear por unidad acumula el error en cada fila y al final el documento no
 * cuadra por unos pesos que nadie sabe de dónde salieron.
 *
 * `factor` reparte un descuento de documento: si el cliente pagó el 92 % de lo
 * que sumaban las líneas, cada línea entra al 92 %. Repartirlo así y no restarlo
 * de una línea cualquiera es lo que hace que el impuesto declarado corresponda
 * a lo que de verdad se cobró.
 */
export function lineaFiscal(l, factor = 1) {
    const tasa = l.impuesto?.tasa ?? 0;
    const bruto = (l.precio_unitario || 0) * (l.cantidad || 0);
    const total = centavos(bruto * factor);
    const base = tasa > 0 ? centavos(total / (1 + tasa)) : total;
    return {
        codigo: l.codigo,
        descripcion: l.descripcion ? `${l.nombre} — ${l.descripcion}` : l.nombre,
        cantidad: l.cantidad,
        base,
        impuesto: {
            tipo: impuestoDIAN(l.impuesto?.tipo ?? 'excluido'),
            tarifa: centavos(tasa * 100),
            valor: centavos(total - base),
        },
    };
}
/**
 * Lo que se regala no va en la factura, pero sí sale del inventario.
 *
 * Pasa a diario: se despachan 140 empanadas y se le ponen 10 de cortesía, o se
 * manda una muestra. Esas unidades **salieron de la nevera** —hay que
 * descontarlas o el inventario miente y el costo de ventas queda corto— y al
 * mismo tiempo **no se le cobran a nadie**.
 *
 * Los dos caminos se separan aquí:
 *
 * - **El pedido las conserva**, con su cantidad y en cero. De ahí salen la
 *   comanda, la descarga de inventario y el costo.
 * - **La factura no las lleva.** Una línea en cero no le dice nada a la DIAN
 *   —ni cambia el total, que es la suma de lo que sí tiene valor— y en cambio
 *   sí puede chocar con las validaciones del anexo técnico, que esperan
 *   cantidades y valores con contenido.
 *
 * El total no se mueve al quitarlas, y eso es justo lo que hace la regla
 * segura: `reparos` compara la suma de las líneas contra los totales
 * declarados, y como lo que se quita vale cero, las dos cuentas siguen dando
 * lo mismo.
 *
 * **Si TODO se regaló, no hay factura que emitir.** Ese caso ya lo atrapa
 * `ventaEnCero`: el documento cierra con comprobante, que es lo que
 * corresponde a una entrega sin cobro.
 *
 * ---
 *
 * **Lo que esta función no decide.** Regalar mercancía puede tener efectos
 * tributarios propios —el retiro de inventario para obsequios tiene su
 * tratamiento— y eso lo define el contador, no este código. Aquí solo se
 * resuelve qué renglones viajan en el documento electrónico; el registro de lo
 * entregado queda completo en el pedido, que es lo que haría falta para
 * sustentarlo.
 */
export function tieneValor(l) {
    return (l.precio_unitario || 0) * (l.cantidad || 0) > 0;
}
/** Una línea que no es producto: el domicilio, la propina. Sin impuesto. */
export function lineaSuelta(codigo, descripcion, valor) {
    return {
        codigo,
        descripcion,
        cantidad: 1,
        base: centavos(valor),
        impuesto: { tipo: 'IVA', tarifa: 0, valor: 0 },
    };
}
export function totalesDeLineas(lineas) {
    const base = centavos(lineas.reduce((s, l) => s + l.base, 0));
    const impuestos = centavos(lineas.reduce((s, l) => s + l.impuesto.valor, 0));
    return { base, impuestos, total: centavos(base + impuestos) };
}
/**
 * El factor con el que se reparte un descuento de documento.
 *
 * Si no hay descuento es 1 y no pasa nada. Si el descuento se comiera el
 * documento entero, el factor es 0 y las líneas quedan en cero — que es
 * absurdo como factura, y por eso `reparos` lo atrapa después en vez de que
 * esto invente un número.
 */
export function factorDescuento(brutoLineas, descuento) {
    if (!descuento || brutoLineas <= 0)
        return 1;
    return Math.max(0, (brutoLineas - descuento) / brutoLineas);
}
// ------------------------------------------------------------- el portero
const TOLERANCIA = 0.02;
/**
 * Todo lo que haría que el servicio devuelva `422`, dicho en español y antes
 * de salir.
 *
 * Duplica a propósito la verificación que hace el servicio. No es desconfianza:
 * es que un `422` llega después de un viaje de ida y vuelta, en inglés de
 * máquina y con el pedido ya cerrado, mientras que un reparo local llega
 * mientras la pantalla todavía está abierta y quien vendió puede arreglarlo.
 *
 * Devuelve la lista vacía cuando el documento está listo para mandarse.
 */
export function reparos(c) {
    const l = [];
    if (!c.referencia_externa?.trim()) {
        l.push('Falta la referencia externa: es el número del pedido y es lo que impide facturar dos veces la misma venta.');
    }
    if (!c.fecha)
        l.push('Falta la fecha del documento.');
    const a = c.adquirente;
    if (!a?.numero_identificacion?.trim()) {
        // El mensaje de antes decía que no se podía facturar a «Consumidor Final».
        // Era falso: para eso está el genérico de la DIAN. Lo que este reparo
        // atrapa es otra cosa —el campo vacío—, y son dos situaciones distintas:
        // «no dieron datos» tiene un número, y `datosFiscalesDeVenta` lo pone.
        // Llegar aquí vacío significa que alguien se saltó ese paso.
        l.push('Falta el documento del cliente. Si nadie dio sus datos, la venta debe ir '
            + `a consumidor final (${CONSUMIDOR_FINAL.documento}), no en blanco.`);
    }
    if (!a?.nombre?.trim())
        l.push('Falta el nombre o la razón social del cliente.');
    if (a && !TIPOS_IDENTIFICACION_DIAN.includes(a.tipo_identificacion)) {
        l.push(`«${a.tipo_identificacion}» no es un tipo de documento con el que se pueda facturar.`);
    }
    if (!c.lineas?.length)
        l.push('El documento no tiene líneas.');
    c.lineas?.forEach((x, i) => {
        const donde = `Línea ${i + 1} (${x.descripcion || x.codigo})`;
        if (!(x.cantidad > 0))
            l.push(`${donde}: la cantidad tiene que ser mayor que cero.`);
        if (x.base < 0)
            l.push(`${donde}: la base no puede ser negativa.`);
        if (!TIPOS_IMPUESTO_DIAN.includes(x.impuesto?.tipo)) {
            l.push(`${donde}: «${x.impuesto?.tipo}» no es un tributo admitido.`);
        }
        // La verificación que hace el servicio, hecha aquí primero.
        const esperado = centavos(x.base * (x.impuesto?.tarifa ?? 0) / 100);
        if (Math.abs(esperado - (x.impuesto?.valor ?? 0)) > TOLERANCIA) {
            l.push(`${donde}: la base por la tarifa da ${esperado} y el impuesto declarado es ${x.impuesto?.valor}.`);
        }
    });
    const suma = totalesDeLineas(c.lineas ?? []);
    if (Math.abs(suma.base - c.totales.base) > TOLERANCIA) {
        l.push(`Las bases de las líneas suman ${suma.base} y el total declarado es ${c.totales.base}.`);
    }
    if (Math.abs(suma.impuestos - c.totales.impuestos) > TOLERANCIA) {
        l.push(`Los impuestos de las líneas suman ${suma.impuestos} y el total declarado es ${c.totales.impuestos}.`);
    }
    if (Math.abs(c.totales.base + c.totales.impuestos - c.totales.total) > TOLERANCIA) {
        l.push('El total no es la base más los impuestos.');
    }
    if (!(c.totales.total > 0))
        l.push('El total del documento tiene que ser mayor que cero.');
    return l;
}
function adquirente(nombre, tipoDoc, documento, correo, direccion) {
    const tipo = identificacionDIAN(tipoDoc);
    return {
        // Si el tipo no se reconoce se manda tal cual para que `reparos` lo atrape
        // con un mensaje que nombra el problema, en vez de que un valor por defecto
        // lo esconda y la DIAN emita a nombre de un tipo de documento que nadie eligió.
        tipo_identificacion: (tipo ?? (tipoDoc || 'NIT')),
        numero_identificacion: (documento ?? '').trim(),
        nombre: nombre.trim(),
        correo: correo?.trim() || undefined,
        direccion: direccion?.trim() || undefined,
        tipo_organizacion: tipo === 'NIT' ? 'PERSONA_JURIDICA' : 'PERSONA_NATURAL',
    };
}
/**
 * El cuerpo de la factura de un pedido.
 *
 * **El domicilio entra como línea propia y sin impuesto.** No es comida: es un
 * servicio de transporte que se cobra aparte, y meterlo dentro de una línea de
 * empanadas le aplicaría el INC del 8 % a algo que no lo lleva.
 *
 * **El descuento se reparte entre todas las líneas y no toca el domicilio.**
 * Un descuento se hace sobre lo que se vendió, no sobre lo que costó llevarlo.
 */
export function facturaDePedido(p) {
    const bruto = p.lineas.reduce((s, l) => s + (l.precio_unitario || 0) * (l.cantidad || 0), 0);
    const factor = factorDescuento(bruto, p.descuento || 0);
    const lineas = p.lineas.filter(tieneValor).map((l) => lineaFiscal(l, factor));
    if (p.domicilio > 0) {
        lineas.push(lineaSuelta('DOMICILIO', 'Servicio de domicilio', p.domicilio));
    }
    return {
        referencia_externa: p.numero,
        fecha: p.fecha_entrega || p.fecha,
        adquirente: adquirente(p.cliente_razon_social || p.cliente_nombre, p.cliente_tipo_documento, p.cliente_documento, p.correo, p.direccion),
        pago: {
            forma: p.forma_pago === 'credito' ? 'CREDITO' : 'CONTADO',
            metodo: p.forma_pago === 'credito' ? 'TRANSFERENCIA' : 'TRANSFERENCIA',
        },
        lineas,
        totales: totalesDeLineas(lineas),
    };
}
/**
 * El cuerpo de la factura de una venta del mostrador.
 *
 * **La propina entra como línea sin impuesto.** Se discutió dejarla afuera
 * —no es ingreso del negocio, es del que atendió— y la razón para incluirla
 * pesa más: una factura cuyo total no es lo que el cliente pagó es una factura
 * que no cuadra con la caja, y eso se descubre justo cuando importa. Va
 * rotulada como propina voluntaria y sin tributo, que es lo que es.
 */
export function facturaDeVenta(v) {
    const bruto = v.lineas.reduce((s, l) => s + (l.precio_unitario || 0) * (l.cantidad || 0), 0);
    const factor = factorDescuento(bruto, v.descuento || 0);
    const lineas = v.lineas.filter(tieneValor).map((l) => lineaFiscal(l, factor));
    if (v.propina > 0) {
        lineas.push(lineaSuelta('PROPINA', 'Propina voluntaria', v.propina));
    }
    return {
        referencia_externa: v.numero,
        fecha: v.fecha,
        adquirente: adquirente(v.cliente_nombre, v.cliente_tipo_documento, v.cliente_documento),
        pago: { forma: 'CONTADO', metodo: metodoPagoDIAN(v.pagos?.[0]?.tipo) },
        lineas,
        totales: totalesDeLineas(lineas),
    };
}
/**
 * La nota crédito de una devolución.
 *
 * Lleva **solo las líneas que se devuelven**, no el documento entero. Una
 * devolución de 20 empanadas de un pedido de 140 no anula el pedido: lo
 * corrige. Anularlo entero y volver a facturar 120 gasta dos números y deja al
 * cliente con tres documentos donde debía haber dos.
 *
 * Sin `documento_referenciado` la DIAN igual la acepta, y por eso hay que
 * insistir: una nota suelta deja el ajuste sin trazabilidad, y dentro de un año
 * nadie sabrá a qué factura correspondía.
 */
export function notaCreditoDe(factura, referencia, motivo, adq, lineas, fecha, pago) {
    return {
        referencia_externa: referencia,
        fecha,
        documento_referenciado: {
            numero: factura.numero, cufe: factura.cufe, fecha: factura.fecha,
        },
        motivo,
        adquirente: adq,
        pago,
        lineas,
        totales: totalesDeLineas(lineas),
    };
}
export const TIPOS_NOTA = [
    {
        valor: 'credito', texto: 'Nota crédito',
        nota: 'Disminuye lo facturado: una devolución, una anulación, un descuento acordado después.',
    },
    {
        valor: 'debito', texto: 'Nota débito',
        nota: 'Aumenta lo facturado: un cargo que faltó, intereses, un ajuste al alza.',
    },
];
export function textoTipoNota(t) {
    return TIPOS_NOTA.find((x) => x.valor === t)?.texto ?? t;
}
export function rutaDeNota(t) {
    return t === 'debito' ? '/v1/notas-debito' : '/v1/notas-credito';
}
/**
 * La referencia de una nota: **suya, y distinta de la de sus hermanas**.
 *
 * No se deriva del número de la factura, y hay dos razones.
 *
 * La primera es que mandar la misma referencia que la factura es decirle al
 * servicio «este documento ya existe» — la idempotencia trabajaría en contra.
 *
 * La segunda aparece con la segunda nota sobre el mismo pedido: si las dos
 * llevaran la misma referencia, la segunda devolvería la primera en vez de
 * emitirse. Por eso lleva su consecutivo.
 *
 * El **número** del documento es otra cosa y no se calcula aquí: las notas
 * tienen serie y prefijo propios —una nota contra `SETT1003` sale como `NCT1`,
 * no `SETT1004`— y lo asigna el servicio.
 */
export function referenciaDeNota(tipo, numeroPedido, consecutivo) {
    return `${tipo === 'debito' ? 'CAR' : 'DEV'}-${numeroPedido}-${consecutivo}`;
}
/**
 * Una línea de cargo: descripción, valor final e impuesto.
 *
 * Es lo que lleva una nota débito. A diferencia de la crédito, no sale de las
 * líneas del pedido: un cargo que faltó cobrar —un domicilio olvidado, un
 * recargo— no es un producto que se vendió de más.
 *
 * El impuesto se saca por resta, igual que en todo lo demás: el valor que se
 * escribe es el final, con el impuesto adentro.
 */
export function lineaDeCargo(descripcion, valorFinal, impuesto) {
    const tasa = impuesto?.tasa ?? 0;
    const total = centavos(valorFinal);
    const base = tasa > 0 ? centavos(total / (1 + tasa)) : total;
    return {
        codigo: 'CARGO',
        descripcion,
        cantidad: 1,
        base,
        impuesto: {
            tipo: impuestoDIAN(impuesto?.tipo ?? 'excluido'),
            tarifa: centavos(tasa * 100),
            valor: centavos(total - base),
        },
    };
}
export const AMBIENTES = [
    {
        valor: 'pruebas', texto: 'Pruebas',
        nota: 'Habilitación ante la DIAN. Los documentos llevan prefijo SETT y no tienen validez fiscal.',
    },
    {
        valor: 'produccion', texto: 'Producción',
        nota: 'Facturación real, con la resolución propia y numeración autorizada.',
    },
];
export const CONFIG_FACTURACION_INICIAL = {
    activa: false,
    tipo_defecto: 'comprobante',
    auto_pedidos: false,
    auto_mostrador: false,
    ambiente: 'pruebas',
};
// -------------------------------------------------- elegido contra reflejo
/**
 * El ambiente en el que de verdad se está facturando.
 *
 * **Hacen falta los dos de acuerdo para decir «producción».** El elegido, que
 * es lo que el administrador configuró, y el reflejo, que es lo que el
 * facturador contesta cuando se le pregunta con esa llave.
 *
 * Que baste uno solo sería el error caro: si el reflejo dijera producción y el
 * elegido no, o al revés, estaríamos facturando en un ambiente que nadie
 * escogió. Y si falta el reflejo —nunca se ha consultado— responde `pruebas`,
 * que es la única dirección segura: marcar de prueba algo real hace que alguien
 * pregunte; dar por real algo de prueba deja al cliente con un papel que su
 * contador no encuentra en la DIAN.
 */
export function ambienteEfectivo(c) {
    if (!c)
        return 'pruebas';
    return c.ambiente === 'produccion' && c.emisor_es_produccion === true
        ? 'produccion'
        : 'pruebas';
}
/**
 * El elegido y el reflejo no concuerdan: **la llave está mal etiquetada**.
 *
 * Quiere decir que la llave guardada como de producción pertenece a un emisor
 * de pruebas, o al revés. Es el error que hay que hacer imposible de pasar por
 * alto, porque no se nota facturando: se nota cuando el contador va a buscar
 * las facturas del mes y no están donde deberían.
 *
 * Sin reflejo no hay desacuerdo, hay ignorancia — y eso se resuelve
 * consultando, no alarmando.
 */
export function desacuerdoDeAmbiente(c) {
    if (!c?.activa)
        return false;
    if (typeof c.emisor_es_produccion !== 'boolean')
        return false;
    return (c.ambiente === 'produccion') !== c.emisor_es_produccion;
}
/**
 * ¿El emisor configurado en el servicio es de verdad esta empresa?
 *
 * Es la única verificación que se puede hacer desde este lado de que una
 * empresa no esté facturando contra el emisor de otra —cargándole a otro sus
 * documentos y su numeración—. El NIT que devuelve el servicio tiene que ser el
 * NIT de la empresa; si no lo es, la llave está apuntando al emisor equivocado.
 *
 * Se comparan solo los dígitos: uno viene con puntos y guion del RUT y el otro
 * no, y esa diferencia de formato no es una diferencia de empresa.
 */
export function emisorCorresponde(nitEmpresa, nitEmisor) {
    const a = (nitEmpresa ?? '').replace(/\D/g, '');
    const b = (nitEmisor ?? '').replace(/\D/g, '');
    if (!a || !b)
        return null;
    // El de la empresa puede traer el dígito de verificación pegado y el del
    // emisor no, o al revés. Basta con que uno empiece por el otro.
    return a === b || a.startsWith(b) || b.startsWith(a);
}
/**
 * ¿A este documento le corresponde factura electrónica?
 *
 * Tres condiciones, y las tres tienen que darse.
 *
 * **La tercera —que haya documento— se conserva aunque el mostrador ya tenga
 * siempre adquiriente**, y la razón es la de antes y no la de ahora. Desde que
 * existe `datosFiscalesDeVenta`, toda venta nueva se guarda con el consumidor
 * final puesto, así que la condición se cumple sola. Lo que atrapa es el
 * pasado: las ventas grabadas antes de ese cambio no tienen documento, y sin
 * esta condición aparecerían de golpe en la bandeja y el barrido intentaría
 * emitirlas sin adquiriente, una por una, hasta que alguien se diera cuenta.
 *
 * Quitarla el día que no quede ninguna de esas es seguro. Quitarla hoy no.
 */
export function leCorrespondeFactura(d, cfg) {
    if (!cfg.activa)
        return false;
    if (!d.cliente_documento?.trim())
        return false;
    /*
     * **Una venta en cero nunca es una factura electrónica.**
     *
     * Existen y son legítimas: la degustación, la reposición de un producto que
     * salió malo. Hay que grabarlas —descuentan inventario y hay que saber cuánto
     * se regaló— pero no hay nada que facturar: la DIAN rechaza un documento en
     * cero, y mandarlo quemaría un consecutivo de la resolución para que lo
     * devuelvan.
     *
     * Se decide aquí, en la misma función que decide todo lo demás, para que la
     * bandeja de pendientes y el barrido lo vean igual que la caja. `reparos` ya
     * lo atrapa antes de enviar, pero eso es la última red: lo que no debe pasar
     * es que la venta aparezca «por facturar» para siempre.
     */
    if (ventaEnCero(d.total))
        return false;
    /*
     * **Lo que vino de otro sistema no se factura aquí.**
     *
     * Las ventas migradas de Bold ya tienen su documento ante la DIAN, emitido
     * por Bold con su resolución. Volver a emitirlas crearía un segundo documento
     * por la misma venta —y un documento ante la DIAN no se borra— además de
     * quemar consecutivos de la resolución propia por ventas de hace dos meses.
     *
     * Entran como comprobante, así que la línea de abajo ya las descartaría. Esto
     * es el segundo candado, y existe porque el primero depende de un campo que
     * alguien podría cambiar desde una pantalla: el día que alguien edite el tipo
     * de documento de una venta vieja, esto la sigue frenando.
     */
    if (d.origen && d.origen !== 'guerrera')
        return false;
    /*
     * **Lo que ya facturó otro no se vuelve a facturar.**
     *
     * El candado de arriba atrapa las ventas que *nacieron* en otro sistema. Este
     * atrapa las que nacieron aquí y se cobraron allá: la cotización se armó en
     * Guerrera, el cliente pagó con el datáfono de Bold, Bold facturó con su
     * resolución. La venta es de aquí —número de aquí, inventario de aquí— y la
     * factura es de allá.
     *
     * Sin esto se queda en «Por facturar» para siempre: le corresponde factura y
     * nunca va a tener una nuestra. Y el problema no es que estorbe en la lista,
     * es lo que pasa el día que alguien se cansa de verla ahí y le da emitir.
     */
    if (facturadaPorFuera(d))
        return false;
    if (d.tipo === 'pedido')
        return cfg.auto_pedidos;
    // En el mostrador manda lo que escogió el cajero, venta por venta. Un
    // comprobante escogido a propósito no es una factura que falta: no tiene por
    // qué aparecer en la bandeja ni que el barrido lo intente cada hora.
    if (d.tipo_documento)
        return d.tipo_documento === 'factura';
    // Las ventas anteriores al selector no traen elección. Para esas sigue
    // decidiendo el interruptor, que es lo que decidía cuando se grabaron.
    return cfg.auto_mostrador;
}
/** Lo que le corresponde factura y todavía no la tiene aceptada. */
export function pendientesDeFacturar(documentos, cfg) {
    return documentos
        .filter((d) => leCorrespondeFactura(d, cfg))
        // `tieneDocumento` y no `facturaLista`: un `EN_PROCESO` ya tiene factura y
        // CUFE. No está pendiente de emisión — lo que le falta es el veredicto, y
        // eso es otra lista con otra acción.
        .filter((d) => !tieneDocumento(d.facturacion))
        .sort((a, b) => (a.fecha ?? '').localeCompare(b.fecha ?? ''));
}
/** Los que ya tienen CUFE y esperan el veredicto de la DIAN. */
export function enValidacion(documentos, cfg) {
    return documentos
        .filter((d) => leCorrespondeFactura(d, cfg))
        .filter((d) => d.facturacion?.estado === 'EN_PROCESO')
        .sort((a, b) => (a.fecha ?? '').localeCompare(b.fecha ?? ''));
}
export function repartirBandeja(l) {
    return {
        automaticos: l.filter((d) => seReintentaSolo(d.facturacion) || soloConsultar(d.facturacion)
            || d.facturacion?.estado === 'ENVIANDO'),
        conPersona: l.filter((d) => esperaPersona(d.facturacion)),
    };
}
// ==========================================================================
//  Notas crédito
// ==========================================================================
export const MOTIVOS_NOTA_CREDITO = [
    { valor: 'devolucion', texto: 'Devolución de mercancía', nota: 'El cliente devolvió producto.' },
    { valor: 'anulacion', texto: 'Anulación de la factura', nota: 'La venta no ocurrió o se facturó por error.' },
    { valor: 'descuento', texto: 'Descuento posterior', nota: 'Se acordó rebajar el precio después de facturar.' },
    { valor: 'error', texto: 'Corrección de un error', nota: 'La factura salió con un dato equivocado.' },
];
export function textoMotivoNota(m) {
    return MOTIVOS_NOTA_CREDITO.find((x) => x.valor === m)?.texto ?? m;
}
export const DESTINOS_MERCANCIA = [
    {
        valor: 'vuelve', texto: 'Volvió y se puede vender',
        nota: 'Entra otra vez al inventario de la sede que escojas.',
    },
    {
        valor: 'perdida', texto: 'Volvió pero no sirve',
        nota: 'Queda registrado como pérdida, con su motivo. No suma al saldo vendible.',
    },
    {
        valor: 'nada', texto: 'No hubo movimiento de mercancía',
        nota: 'Corrección de precio o de un dato. El inventario no se toca.',
    },
];
export function textoDestino(d) {
    return DESTINOS_MERCANCIA.find((x) => x.valor === d)?.texto ?? d;
}
export function notaCreditoDeDocumento(p, factura, devueltas, motivo, fecha, consecutivo = 1) {
    const bruto = p.lineas.reduce((s, l) => s + (l.precio_unitario || 0) * (l.cantidad || 0), 0);
    const factor = factorDescuento(bruto, p.descuento || 0);
    const lineas = p.lineas
        .map((l) => {
        const d = devueltas.find((x) => x.codigo === l.codigo);
        if (!d || d.cantidad <= 0)
            return null;
        // La cantidad devuelta nunca puede pasarse de la vendida: una nota por
        // más de lo facturado le devuelve al cliente plata que nunca pagó.
        const cantidad = Math.min(d.cantidad, l.cantidad);
        return lineaFiscal({ ...l, cantidad }, factor);
    })
        .filter((x) => x !== null);
    /*
     * **El domicilio y la propina vuelven solo si vuelve todo.**
     *
     * Son líneas de la factura que no son mercancía, así que no aparecen en la
     * tabla de qué se devuelve. Pero si el cliente devuelve el pedido entero, se
     * le cobró un domicilio por algo que no se quedó, y la propina fue por un
     * servicio que terminó deshecho: dejarlos fuera le acredita de menos.
     *
     * En una devolución parcial se quedan, y es lo correcto: el domicilio sí se
     * prestó y la propina no es proporcional a las unidades.
     *
     * «Todo» es todo: cada línea devuelta por su cantidad completa.
     */
    const vuelveTodo = p.lineas.length > 0 && p.lineas.every((l) => {
        const d = devueltas.find((x) => x.codigo === l.codigo);
        return (d?.cantidad ?? 0) >= l.cantidad;
    });
    if (vuelveTodo) {
        if ((p.domicilio ?? 0) > 0) {
            lineas.push(lineaSuelta('DOMICILIO', 'Servicio de domicilio', p.domicilio));
        }
        if ((p.propina ?? 0) > 0) {
            lineas.push(lineaSuelta('PROPINA', 'Propina voluntaria', p.propina));
        }
    }
    const base = notaCreditoDe({ numero: factura.numero, cufe: factura.cufe, fecha: factura.fecha }, referenciaDeNota('credito', p.numero, consecutivo), motivo, 
    /*
     * **De la factura, no del pedido.** Si la factura no lo trae —emitida antes
     * de que se guardara— se cae al del pedido, que es lo que había antes y
     * sigue siendo lo mejor disponible para esos casos.
     */
    factura.adquirente ?? adquirente(p.cliente_razon_social || p.cliente_nombre || '', p.cliente_tipo_documento, p.cliente_documento, p.correo, p.direccion), lineas, fecha, {
        forma: p.forma_pago === 'credito' ? 'CREDITO' : 'CONTADO',
        metodo: 'TRANSFERENCIA',
    });
    return base;
}
/**
 * El cuerpo de una nota débito: **un cargo**, no una devolución.
 *
 * No sale de las líneas del pedido a propósito. Una nota débito nace de plata
 * que faltó cobrar —un domicilio que no se facturó, un recargo acordado
 * después— y eso no es un producto que se vendió de más. Pedirle al usuario que
 * escoja líneas del pedido lo obligaría a expresar un cargo como si fuera
 * mercancía, y el documento diría algo que no pasó.
 *
 * Tampoco lleva pregunta de inventario: no vuelve nada a la nevera.
 */
export function notaDebitoDeDocumento(p, factura, cargo, motivo, fecha, consecutivo = 1) {
    const lineas = [lineaDeCargo(cargo.descripcion, cargo.valor, cargo.impuesto)];
    return notaCreditoDe({ numero: factura.numero, cufe: factura.cufe, fecha: factura.fecha }, referenciaDeNota('debito', p.numero, consecutivo), motivo, 
    /*
     * **De la factura, no del pedido.** Si la factura no lo trae —emitida antes
     * de que se guardara— se cae al del pedido, que es lo que había antes y
     * sigue siendo lo mejor disponible para esos casos.
     */
    factura.adquirente ?? adquirente(p.cliente_razon_social || p.cliente_nombre || '', p.cliente_tipo_documento, p.cliente_documento, p.correo, p.direccion), lineas, fecha, {
        forma: p.forma_pago === 'credito' ? 'CREDITO' : 'CONTADO',
        metodo: 'TRANSFERENCIA',
    });
}
/**
 * Las notas de un pedido, tolerando el formato viejo de una sola.
 *
 * El campo se llama `notas_fiscales` y no `notas` porque `notas` ya era el
 * texto libre del pedido —«tocar el timbre dos veces»— desde mucho antes. Dos
 * cosas distintas con el mismo nombre en el mismo documento es cómo se pierde
 * una de las dos; lo atrapó el compilador al primer intento.
 */
export function notasDe(p) {
    if (p?.notas_fiscales?.length)
        return p.notas_fiscales;
    // Se lee para que un pedido viejo no pierda su nota al cambiar la forma de
    // guardarlas. Le faltan los campos que entonces no existían —tipo,
    // referencia, consecutivo— y se le ponen los que le habrían tocado.
    if (!p?.nota_credito)
        return [];
    return [{
            tipo: 'credito',
            referencia: p.nota_credito.referencia ?? p.nota_credito.numero ?? 'anterior',
            consecutivo: 1,
            ...p.nota_credito,
        }];
}
/** El consecutivo que le toca a la próxima nota de este pedido. */
export function siguienteConsecutivoNota(notas) {
    return notas.reduce((max, n) => Math.max(max, n.consecutivo ?? 0), 0) + 1;
}
/**
 * Lo que impide mandar una nota crédito mal armada.
 *
 * La comprobación de que hay `documento_referenciado` es la que más importa y
 * la que la DIAN **no** hace: acepta una nota sin referencia, y entonces queda
 * un ajuste que no se sabe a qué factura corresponde. Dentro de un año eso es
 * una cifra suelta en el estado de resultados que nadie puede explicar.
 */
export function reparosNota(c, saldo) {
    const l = reparos(c);
    if (!c.documento_referenciado?.numero) {
        l.push('Falta el número de la factura que se corrige.');
    }
    if (!c.documento_referenciado?.cufe) {
        l.push('Falta el CUFE de la factura original: sin él la nota queda sin trazabilidad.');
    }
    if (!c.motivo?.trim())
        l.push('Falta el motivo de la nota.');
    /*
     * El tope, comprobado aquí antes de que la petición salga.
     *
     * El facturador lo rechaza con `422 NOTA_EXCEDE_FACTURA`, y eso está bien
     * como última red. Pero un reparo local llega en español, con la pantalla
     * todavía abierta y el valor todavía editable; un 422 llega después de un
     * viaje de ida y vuelta, cuando la persona ya cerró el formulario.
     *
     * Solo se comprueba si se sabe el saldo. Con `null` —factura emitida antes de
     * que se guardara su total— no se inventa un tope: se deja pasar y que el
     * facturador decida, que es quien de verdad sabe.
     */
    if (typeof saldo === 'number' && c.totales?.total > saldo + TOLERANCIA) {
        l.push(`La nota excede lo que queda por acreditar de la factura `
            + `${c.documento_referenciado?.numero ?? ''}: quedan ${saldo} y esta nota `
            + `es por ${c.totales.total}.`);
    }
    return l;
}
